import { useEffect, useState } from 'react';
import { Row, Col, Table, Button, Form, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import { useMsal } from '@azure/msal-react';
import { EnhetViewModel } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import EnhetKildeApi from 'services/KjederegisteretAdminApi/EnhetKildeApi';
import { EnhetKildeViewModel } from 'services/KjederegisteretAdminApi/EnhetKildeApi/types';
import EnhetMatchLookupApi from 'services/KjederegisteretAdminApi/EnhetMatchLookupApi';
import {
  EnhetLookupContext,
  EnhetLookupStepName,
  LookupCandidate,
} from 'services/KjederegisteretAdminApi/EnhetMatchLookupApi/types';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import useToaster from 'utils/hooks/useToaster';
import HttpErrorHelper from 'utils/HttpErrorHelper';
import ConfirmButton from 'components/ConfirmButton';
import {
  KILDE_LABELS,
  buildKildeLink,
} from 'services/KjederegisteretAdminApi/EnhetKildeApi/constants';

interface Props {
  enhet: EnhetViewModel;
  onUpdated: () => void;
}

const Kilder = (props: Props) => {
  const toaster = useToaster();
  const { accounts } = useMsal();
  const currentUser = (accounts[0] && accounts[0].name) || 'ukjent';
  const isMountedRef = useIsMountedRef();

  const [kilder, setKilder] = useState<EnhetKildeViewModel[]>([]);

  const [activeSearchStep, setActiveSearchStep] = useState<EnhetLookupStepName | undefined>();
  const [searchResults, setSearchResults] = useState<LookupCandidate[]>([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>();
  const [isSearching, setIsSearching] = useState(false);
  const [isLinking, setIsLinking] = useState(false);

  const loadKilder = async () => {
    const result = await EnhetKildeApi.getKilder(props.enhet.id);
    if (isMountedRef.current) setKilder(result);
  };

  useEffect(() => {
    loadKilder();
    // props.enhet (not just .id) so a re-fetch after linking/unlinking elsewhere (e.g. the inline
    // suggestion on Basis) is picked up too - .id alone never changes on a same-Enhet refresh.
  }, [props.enhet]);

  const buildContext = (): EnhetLookupContext => ({
    organisasjonsnummer: props.enhet.firma?.organisasjonsnr,
    bedriftsnr: props.enhet.firma?.bedriftsnr,
    enhetNavn: props.enhet.enhetnavn,
    gateadresse: props.enhet.besoksadresse?.gateadresse,
    postnummer: props.enhet.besoksadresse?.postnr,
    poststed: props.enhet.besoksadresse?.poststed,
    latitude: props.enhet.besoksadresse?.latitude,
    longitude: props.enhet.besoksadresse?.longitude,
    hjemmeside: props.enhet.hjemmeside,
  });

  const handleSearchClicked = async (step: EnhetLookupStepName) => {
    setActiveSearchStep(step);
    setSearchResults([]);
    setSelectedCandidateId(undefined);
    setIsSearching(true);
    try {
      const results = await EnhetMatchLookupApi.findCandidates(step, buildContext());
      if (isMountedRef.current) setSearchResults(results);
    } catch (err) {
      toaster.error('Noe gikk galt', HttpErrorHelper.formatError(err));
    } finally {
      if (isMountedRef.current) setIsSearching(false);
    }
  };

  const handleAvbrytSok = () => {
    setActiveSearchStep(undefined);
    setSearchResults([]);
    setSelectedCandidateId(undefined);
  };

  const handleLinkClicked = async () => {
    if (!activeSearchStep || !selectedCandidateId) return;
    setIsLinking(true);
    try {
      const confirmResult = await EnhetMatchLookupApi.confirm(
        activeSearchStep,
        buildContext(),
        selectedCandidateId,
      );
      await EnhetKildeApi.tilknytt({
        enhetId: props.enhet.id,
        kilde: activeSearchStep,
        eksternId: confirmResult.details.id,
        visningsnavn: confirmResult.details.displayName,
        tilknyttetAv: currentUser,
      });
      toaster.success('Kilde tilknyttet', `${KILDE_LABELS[activeSearchStep]} er nå tilknyttet.`);
      handleAvbrytSok();
      await loadKilder();
      props.onUpdated();
    } catch (err) {
      toaster.error('Noe gikk galt', HttpErrorHelper.formatError(err));
    } finally {
      setIsLinking(false);
    }
  };

  const handleFjernClicked = async (kilde: EnhetKildeViewModel) => {
    await EnhetKildeApi.fjern({ id: kilde.id });
    await loadKilder();
    props.onUpdated();
  };

  return (
    <div className="container-fluid">
      <Row className="mb-3">
        <Col>
          <Button
            variant="outline-primary"
            className="me-2"
            disabled={isSearching}
            onClick={() => handleSearchClicked('brreg-underenhet')}
          >
            Søk i Brreg underenhet
          </Button>
          <Button
            variant="outline-primary"
            className="me-2"
            disabled={isSearching}
            onClick={() => handleSearchClicked('google-places')}
          >
            Søk i Google Places
          </Button>
          <Button
            variant="outline-primary"
            disabled={isSearching || !props.enhet.hjemmeside}
            title={!props.enhet.hjemmeside ? 'Krever at hjemmeside er satt' : undefined}
            onClick={() => handleSearchClicked('facebook')}
          >
            Søk etter Facebook-side
          </Button>
        </Col>
      </Row>

      {activeSearchStep && (
        <Row className="mb-3">
          <Col>
            <h5>Søkeresultater - {KILDE_LABELS[activeSearchStep]}</h5>
            {isSearching ? (
              <Spinner animation="border" size="sm" />
            ) : searchResults.length === 0 ? (
              <div className="text-muted mb-2">Ingen treff.</div>
            ) : (
              searchResults.map((c) => {
                const link = buildKildeLink(activeSearchStep, c.id);
                return (
                  <Form.Check
                    key={c.id}
                    type="radio"
                    id={`kilde-candidate-${c.id}`}
                    name="kilde-candidate"
                    className="mb-2"
                    checked={selectedCandidateId === c.id}
                    onChange={() => setSelectedCandidateId(c.id)}
                    label={
                      <span>
                        {link ? (
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <strong>{c.displayName}</strong>
                          </a>
                        ) : (
                          <strong>{c.displayName}</strong>
                        )}
                        <br />
                        <small className="text-muted">
                          {[c.fields.adresse ?? c.fields.gateadresse, c.fields.poststed]
                            .filter(Boolean)
                            .join(', ')}
                        </small>
                      </span>
                    }
                  />
                );
              })
            )}
            <div className="mt-2">
              <Button variant="outline-secondary" className="me-2" onClick={handleAvbrytSok}>
                Avbryt
              </Button>
              <Button
                variant="success"
                disabled={!selectedCandidateId || isLinking}
                onClick={handleLinkClicked}
              >
                {isLinking ? <Spinner animation="border" size="sm" /> : 'Koble til'}
              </Button>
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Table hover>
            <thead>
              <tr>
                <th>Kilde</th>
                <th>Navn</th>
                <th>Tilknyttet</th>
                <th>Av</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {kilder.map((k) => {
                const link = buildKildeLink(k.kilde, k.eksternId);
                return (
                  <tr key={k.id}>
                    <td>{KILDE_LABELS[k.kilde] ?? k.kilde}</td>
                    <td>
                      {link ? (
                        <a href={link} target="_blank" rel="noopener noreferrer">
                          {k.visningsnavn || k.eksternId}
                        </a>
                      ) : (
                        k.visningsnavn || k.eksternId
                      )}
                    </td>
                    <td>{format(k.tilknyttetDato, 'dd.MM.yyyy')}</td>
                    <td data-private>{k.tilknyttetAv}</td>
                    <td className="fitContent">
                      <ConfirmButton
                        onClick={() => handleFjernClicked(k)}
                        variant="outline-secondary"
                        size="sm"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  );
};

export default Kilder;
